// src/data/spacedRepetition.js
// Spaced Repetition für Vokabeln: Sobald ein Wort Stufe 5 („sehr sicher“) erreicht,
// kommt es nicht mehr mit seiner Woche dran, sondern wochenübergreifend in wachsenden
// Abständen (1 → 2 → 4 → 8 … Tage, höchstens MAX_INTERVAL_DAYS).
//
// Felder im Fortschritt eines Wortes (progress[id]):
//   srStage – Wiederholungsstufe (1 = nächstes Intervall 1 Tag, 2 = 2 Tage, …)
//   srDueAt – Zeitpunkt (ms), ab dem die nächste Wiederholung fällig ist

const DAY_MS = 24 * 60 * 60 * 1000;

export const MAX_INTERVAL_DAYS = 60;

// Höchstens dieser Anteil einer Sitzung besteht aus Wiederholungen.
export const SR_SESSION_SHARE = 0.25;

export function intervalDays(stage) {
  const s = Math.max(1, stage || 1);
  return Math.min(2 ** (s - 1), MAX_INTERVAL_DAYS);
}

function scheduleStage(state, stage, now) {
  return {
    ...state,
    srStage: stage,
    srDueAt: now + intervalDays(stage) * DAY_MS,
  };
}

function clearSchedule(state) {
  const { srStage, srDueAt, ...rest } = state;
  return rest;
}

export function isInReview(state) {
  return !!state && (state.box === 5 || typeof state.srDueAt === "number");
}

/**
 * Fällig = in der Wiederholung und der Termin ist erreicht. Stufe-5-Wörter ohne Termin
 * (Fortschritt aus der Zeit vor Spaced Repetition) gelten als sofort fällig.
 */
export function isDue(state, now = Date.now()) {
  if (!isInReview(state)) return false;
  if (typeof state.srDueAt !== "number") return true;
  return state.srDueAt <= now;
}

/**
 * Passt den Wiederholplan an, nachdem ein Wort beantwortet wurde.
 *   prevState  – Fortschritt vor der Antwort
 *   nextState  – Fortschritt nach dem normalen Stufen-Update (box schon angepasst)
 *   wasDue     – das Wort kam als fällige Wiederholung dran
 *   inWeek     – das Wort gehört zum gerade geübten Wochenset
 */
export function applyReviewResult({ prevState, nextState, isRight, wasDue, inWeek, now = Date.now() }) {
  if (wasDue) {
    if (isRight) {
      // Stufe-5-Wörter ohne Termin starten mit dem ersten Intervall.
      const stage = typeof prevState?.srDueAt === "number" ? (prevState.srStage || 1) + 1 : 1;
      return scheduleStage(nextState, stage, now);
    }
    // Falsch: eine Stufe runter (nextState.box), höchstens Stufe 4 – auch für Wörter unter
    // Stufe 5, die z. B. nach der Blitzabfrage in der Wiederholung sind. Wörter anderer
    // Wochen bleiben mit 1 Tag in der Wiederholung, sonst kämen sie nie wieder dran.
    const demoted = { ...nextState, box: Math.min(nextState.box ?? 4, 4) };
    return inWeek ? clearSchedule(demoted) : scheduleStage(demoted, 1, now);
  }

  if (nextState.box === 5) {
    // Neu auf Stufe 5 → Wiederholung startet in 1 Tag. Wer ein nicht fälliges Stufe-5-Wort
    // zusätzlich übt, verschiebt damit seinen Termin nicht.
    if (prevState?.box !== 5 || typeof prevState?.srDueAt !== "number") {
      return scheduleStage(nextState, 1, now);
    }
    return nextState;
  }

  // Unter Stufe 5 gefallen (z. B. zusätzlich geübt und falsch) → wieder normaler Wochenmodus.
  return inWeek ? clearSchedule(nextState) : nextState;
}

/**
 * Wörter, deren Wiederholung fällig ist, die am längsten überfälligen zuerst.
 *   progress – Fortschritt aller Wörter (id → state)
 *   wordsById – Map id → Wort-Objekt (mit source/target)
 */
export function collectDueWords(progress, wordsById, now = Date.now()) {
  return Object.entries(progress || {})
    .filter(([id, state]) => wordsById.has(id) && isDue(state, now))
    .sort(([, a], [, b]) => (a.srDueAt ?? 0) - (b.srDueAt ?? 0))
    .map(([id]) => wordsById.get(id));
}

export function maxReviewsPerSession(sessionSize) {
  return Math.floor(sessionSize * SR_SESSION_SHARE);
}
